import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAccount, useConnect, useDisconnect, useSignMessage, useSwitchChain } from "wagmi";
import { base } from "wagmi/chains";
import { Footer } from "../components/Footer";
import { Nav } from "../components/Nav";
import { X402DiscoveryConsent } from "../components/x402/X402DiscoveryConsent";
import { X402LinkedIdentityNotice } from "../components/x402/X402LinkedIdentityNotice";
import { X402WalletLinkPanel } from "../components/x402/X402WalletLinkPanel";
import { shortAddr } from "../lib/api";
import {
  agentCashConfirmationCommand,
  createX402Profile,
  fetchX402LinkedIdentities,
  fetchPrivateX402Profile,
  fetchX402DiscoveryPreview,
  prepareX402WalletLink,
  revokeX402WalletLink,
  setX402DiscoveryMode,
  toBaseAccountId,
  type X402DiscoveryPreview,
  type X402LinkedIdentity,
  type X402Profile,
  type X402SiwxSigner,
  type X402WalletLinkPreparation,
} from "../lib/x402Profile";

type Operation = "idle" | "connecting" | "opening" | "linking" | "refreshing" | "revoking" | "preview" | "publishing";
type ErrorArea = "page" | "link" | "discovery";

export function X402ProfileSettingsPage() {
  const { address, chainId, isConnected } = useAccount();
  const { connectors, connectAsync } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { switchChainAsync } = useSwitchChain();
  const { signMessageAsync } = useSignMessage();
  const [profile, setProfile] = useState<X402Profile | null>(null);
  const [preparation, setPreparation] = useState<X402WalletLinkPreparation | null>(null);
  const [memberAddress, setMemberAddress] = useState("");
  const [preview, setPreview] = useState<X402DiscoveryPreview | null>(null);
  const [linkedIdentities, setLinkedIdentities] = useState<X402LinkedIdentity[]>([]);
  const [identityLookupFailed, setIdentityLookupFailed] = useState(false);
  const [operation, setOperation] = useState<Operation>("idle");
  const [error, setError] = useState<string | null>(null);
  const [errorArea, setErrorArea] = useState<ErrorArea>("page");
  const connector = useMemo(
    () => connectors.find((candidate) => /metamask/i.test(candidate.name) || candidate.id === "metaMask" || candidate.type === "metaMask") ??
      connectors.find((candidate) => candidate.type === "injected") ?? connectors[0],
    [connectors],
  );

  useEffect(() => {
    let current = true;
    setLinkedIdentities([]);
    setIdentityLookupFailed(false);
    if (!address) return () => { current = false; };
    void fetchX402LinkedIdentities(address)
      .then((identities) => { if (current) setLinkedIdentities(identities); })
      .catch(() => {
        if (current) {
          setLinkedIdentities([]);
          setIdentityLookupFailed(true);
        }
      });
    return () => { current = false; };
  }, [address]);

  useEffect(() => {
    if (!profile || !address || !profile.controllerAccount) return;
    if (profile.controllerAccount.toLowerCase() === toBaseAccountId(address).toLowerCase()) return;
    setProfile(null);
    setPreparation(null);
    setPreview(null);
    setErrorArea("page");
    setError("The connected account changed. Open the profile controlled by this wallet before continuing.");
  }, [address, profile]);

  async function connectWallet() {
    setErrorArea("page");
    if (!connector) {
      setError("No compatible EVM wallet was found. Install or open a Base-compatible wallet, then try again.");
      return;
    }
    setError(null);
    setOperation("connecting");
    try {
      await connectAsync({ connector });
    } catch (cause) {
      setError(messageFrom(cause));
    } finally {
      setOperation("idle");
    }
  }

  async function withSigner<T>(run: (signer: X402SiwxSigner) => Promise<T>): Promise<T> {
    if (!address) throw new Error("Connect the controller wallet first.");
    if (chainId !== base.id) await switchChainAsync({ chainId: base.id });
    return run({
      address,
      signMessage: (message) => signMessageAsync({ account: address, message }),
    });
  }

  async function openProfile() {
    setErrorArea("page");
    setError(null);
    setOperation("opening");
    try {
      const result = await withSigner((signer) => createX402Profile(signer));
      setProfile(result);
      setPreparation(null);
      setPreview(null);
    } catch (cause) {
      setError(messageFrom(cause));
    } finally {
      setOperation("idle");
    }
  }

  async function prepareLink() {
    if (!profile) return;
    setErrorArea("link");
    setError(null);
    setOperation("linking");
    try {
      if (profile.discoveryMode !== "public_all") {
        throw new Error("Publish the controller-only profile before preparing a member wallet link.");
      }
      const memberAccount = toBaseAccountId(memberAddress.trim());
      if (profile.wallets.some((wallet) => wallet.accountId.toLowerCase() === memberAccount.toLowerCase())) {
        throw new Error("That wallet is already active in this profile.");
      }
      const result = await withSigner((signer) => prepareX402WalletLink(profile.id, memberAccount, signer));
      setPreparation(result);
      setPreview(null);
    } catch (cause) {
      setError(messageFrom(cause));
    } finally {
      setOperation("idle");
    }
  }

  async function refreshProfile() {
    if (!profile) return;
    setErrorArea("link");
    setError(null);
    setOperation("refreshing");
    try {
      const result = await withSigner((signer) => fetchPrivateX402Profile(profile.id, signer));
      setProfile(result);
      setPreview(null);
      if (preparation && result.wallets.some((wallet) => wallet.accountId.toLowerCase() === preparation.link.memberAccount.toLowerCase())) {
        setPreparation(null);
        setMemberAddress("");
      } else if (preparation) {
        setError("The member proof is still pending. Run the AgentCash command, then check again before it expires.");
      }
    } catch (cause) {
      setError(messageFrom(cause));
    } finally {
      setOperation("idle");
    }
  }

  async function revokeLink(accountId: string) {
    if (!profile) return;
    setErrorArea("link");
    setError(null);
    setOperation("revoking");
    try {
      const result = await withSigner((signer) => revokeX402WalletLink(profile.id, accountId, signer));
      setProfile(result);
      setPreparation(null);
      setPreview(null);
    } catch (cause) {
      setError(messageFrom(cause));
    } finally {
      setOperation("idle");
    }
  }

  async function loadPreview() {
    if (!profile) return;
    setErrorArea("discovery");
    setError(null);
    setOperation("preview");
    try {
      const result = await withSigner((signer) => fetchX402DiscoveryPreview(profile.id, signer));
      setPreview(result);
    } catch (cause) {
      setError(messageFrom(cause));
    } finally {
      setOperation("idle");
    }
  }

  async function updateDiscovery(mode: "private" | "public_all", previewDigest?: string) {
    if (!profile) return;
    setErrorArea("discovery");
    setError(null);
    setOperation("publishing");
    try {
      const result = await withSigner((signer) => setX402DiscoveryMode(profile.id, mode, signer, previewDigest));
      setProfile(result.profile);
      setPreview(null);
    } catch (cause) {
      setPreview(null);
      setError(`${messageFrom(cause)} Review a fresh public scope preview before trying again.`);
    } finally {
      setOperation("idle");
    }
  }

  const busy = operation !== "idle";
  const command = preparation ? agentCashConfirmationCommand(preparation) : null;

  return (
    <>
      <Nav current="x402" />
      <main className="overflow-x-clip">
        <section className="px-6 md:px-10 pt-10 md:pt-16 max-w-6xl mx-auto">
          <Link to="/x402" className="btn-mono text-xs text-muted hover:text-ink">← Recognized endpoints</Link>
          <header className="mt-7 max-w-4xl">
            <p className="btn-mono text-xs uppercase tracking-[0.18em] text-muted">x402 wallet profile</p>
            <h1 className="mt-3 text-4xl md:text-6xl font-display tracking-tight leading-[0.98]">Manage your x402 wallet profile.</h1>
            <p className="mt-5 text-base md:text-lg text-muted leading-relaxed max-w-3xl">
              Connect Base wallets that each prove control. Linking never grants spending authority.
            </p>
          </header>
          <ol className="mt-8 grid gap-px overflow-hidden rounded-md border border-faint bg-faint sm:grid-cols-3" aria-label="Profile setup steps">
            <WorkflowStep number="1" label="Open controller profile" />
            <WorkflowStep number="2" label="Review public scope" />
            <WorkflowStep number="3" label="Link member wallet" />
          </ol>
        </section>

        <section className="px-6 md:px-10 mt-9 max-w-6xl mx-auto">
          {!isConnected ? (
            <div className="border-y border-faint py-9">
              <h2 className="text-2xl font-display">Connect the controller wallet</h2>
              <p className="mt-2 max-w-2xl text-sm text-muted leading-relaxed">
                The controller manages membership and public discovery. Adding another wallet requires publishing the controller profile first, after you review its public scope.
              </p>
              <button className="btn btn-primary mt-5" type="button" onClick={() => void connectWallet()} disabled={busy}>
                {operation === "connecting" ? "Connecting…" : connector ? `Connect ${connector.name}` : "Connect wallet"}
              </button>
            </div>
          ) : !profile ? (
            <div className="border-y border-faint py-9">
              <p className="btn-mono text-xs text-muted">Connected controller · {address ? shortAddr(address) : "unknown"}</p>
              <h2 className="mt-2 text-2xl font-display">Open your private profile</h2>
              <p className="mt-2 max-w-2xl text-sm text-muted leading-relaxed">
                Sign one identity-only message for {address ? toBaseAccountId(address) : "this wallet"}. Boon creates the profile privately if needed. Nothing is published, transferred, approved, or paid.
              </p>
              <X402LinkedIdentityNotice identities={linkedIdentities} unavailable={identityLookupFailed} />
              <div className="mt-5 flex flex-wrap gap-3">
                <button className="btn btn-primary" type="button" onClick={() => void openProfile()} disabled={busy}>
                  {operation === "opening" ? "Review signature…" : "Open wallet profile"}
                </button>
                <button className="btn btn-ghost" type="button" onClick={() => void disconnectAsync()}>Disconnect</button>
              </div>
            </div>
          ) : (
            <>
              <div className="border-y border-faint py-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">Profile</p>
                  <p className="mt-1 btn-mono text-sm break-all">{profile.id}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {profile.discoveryMode === "public_all" && <Link className="btn btn-ghost" to={`/x402/profiles/${profile.id}`}>View public profile</Link>}
                  <button className="btn btn-ghost" type="button" onClick={() => void openProfile()} disabled={busy}>Refresh profile</button>
                </div>
              </div>
              <X402DiscoveryConsent
                profile={profile}
                preview={preview}
                busy={operation === "preview" || operation === "publishing"}
                error={errorArea === "discovery" ? error : null}
                onLoadPreview={() => void loadPreview()}
                onEnable={(digest) => void updateDiscovery("public_all", digest)}
                onDisable={() => void updateDiscovery("private")}
              />
              <X402WalletLinkPanel
                profile={profile}
                memberAddress={memberAddress}
                preparation={preparation}
                agentCashCommand={command}
                busy={operation === "linking" || operation === "refreshing" || operation === "revoking"}
                error={errorArea === "link" ? error : null}
                onMemberAddressChange={(value) => {
                  setMemberAddress(value);
                  setPreparation(null);
                  setErrorArea("link");
                  setError(null);
                }}
                onPrepare={() => void prepareLink()}
                onRefresh={() => void refreshProfile()}
                onRevoke={(accountId) => void revokeLink(accountId)}
              />
            </>
          )}
          {error && errorArea === "page" && <p className="mt-4 text-sm text-danger" role="alert">{error}</p>}
        </section>
      </main>
      <Footer />
    </>
  );
}

function WorkflowStep({ number, label }: { number: string; label: string }) {
  return (
    <li className="flex items-center gap-3 bg-paper px-4 py-3">
      <span className="btn-mono text-xs text-olive-deep" aria-hidden="true">{number.padStart(2, "0")}</span>
      <span className="text-sm text-ink">{label}</span>
    </li>
  );
}

function messageFrom(value: unknown): string {
  return value instanceof Error ? value.message : String(value);
}
